import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { queueCartChange, guardAdditions } from '@theme/product-weight-limits';

/**
 * @typedef {object} QuickReorderRefs
 * @property {HTMLFormElement} form
 * @property {HTMLButtonElement} [submitButton]
 * @property {HTMLButtonElement} [selectAllButton]
 * @property {HTMLInputElement[]} [variantCheckboxes]
 * @property {HTMLElement} [total]
 * @property {HTMLElement} [status]
 */

/**
 * Quick reorder form component for bulk adding variants to the cart.
 *
 * @extends {Component<QuickReorderRefs>}
 */
class QuickReorderComponent extends Component {
  /** @type {string | undefined} */
  #defaultSubmitLabel;

  connectedCallback() {
    super.connectedCallback();

    const { submitButton } = this.refs;
    this.#defaultSubmitLabel = submitButton?.textContent?.trim();

    this.updateTotal();
    this.#syncSelectAllButton();
    this.#syncVariantRows();
  }

  /**
   * Handles checkbox changes for variants.
   * @param {Event & { target: EventTarget }} event
   */
  handleVariantToggle(event) {
    const target = /** @type {EventTarget | null} */ (event.target);

    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'checkbox' || !target.dataset.variantId) return;

    this.updateTotal();
    this.#syncSelectAllButton();
    this.#syncVariantRows();
  }

  /** Handles the select-all toggle button. */
  handleSelectAll() {
    const shouldSelectAll = !this.#areAllSelected();
    for (const checkbox of this.#availableCheckboxes()) {
      checkbox.checked = shouldSelectAll;
    }

    this.updateTotal();
    this.#syncSelectAllButton();
    this.#syncVariantRows();
  }

  /**
   * Handles form submission and posts selected variants to the cart.
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const items = [];

    for (const checkbox of this.#availableCheckboxes()) {
      if (!checkbox.checked) continue;

      const variantId = Number.parseInt(checkbox.dataset.variantId ?? '0', 10);
      if (!variantId || Number.isNaN(variantId)) continue;

      items.push({
        id: variantId,
        quantity: 1,
      });
    }

    if (items.length === 0) {
      this.#announce('Select at least one item before adding to your cart.', 'error');
      return;
    }

    this.#setLoading(true);

    try {
      const response = await queueCartChange(async () => {
        await guardAdditions(this, items);
        return fetch(`${Theme.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data?.message || data?.description || 'Unable to add items to the cart.';
        this.#announce(message, 'error');
        document.dispatchEvent(new CartErrorEvent(this.dataset.sectionId ?? '', message));
        return;
      }

      const totalQuantity = items.length;

      for (const checkbox of this.#availableCheckboxes()) {
        checkbox.checked = false;
      }

      this.updateTotal();
      this.#syncSelectAllButton();
      this.#syncVariantRows();

      this.#announce(`Added ${totalQuantity} item${totalQuantity === 1 ? '' : 's'} to your cart.`, 'success');

      document.dispatchEvent(
        new CartAddEvent(data, this.dataset.sectionId ?? '', {
          source: 'quick-reorder',
          itemCount: totalQuantity,
        })
      );
    } catch (error) {
      console.error('Quick reorder failed', error);
      this.#announce(error.message || 'Something went wrong. Please try again.', 'error');
      document.dispatchEvent(new CartErrorEvent(this.dataset.sectionId ?? '', 'Quick reorder failed'));
    } finally {
      this.#setLoading(false);
    }
  }

  /** Updates the total price preview. */
  updateTotal() {
    const { total } = this.refs;
    if (!total) return;

    const checkboxes = this.#availableCheckboxes();

    const cents = checkboxes.reduce((sum, checkbox) => {
      if (!checkbox.checked) return sum;

      const price = Number.parseInt(checkbox.dataset.variantPrice ?? '0', 10);
      if (Number.isNaN(price)) return sum;

      return sum + price;
    }, 0);

    total.textContent = this.#formatMoney(cents);

    const { submitButton, status } = this.refs;
    if (submitButton) {
      submitButton.toggleAttribute('disabled', cents === 0);
    }

    if (status && cents > 0) {
      status.hidden = true;
    }
  }

  /**
   * Updates the status message.
   * @param {string} message
   * @param {'success' | 'error'} state
   */
  #announce(message, state) {
    const { status } = this.refs;
    if (!status) return;

    status.hidden = false;
    status.textContent = message;
    status.classList.toggle('is-success', state === 'success');
    status.classList.toggle('is-error', state === 'error');
  }

  /**
   * Toggles loading state on the submit button.
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const { submitButton } = this.refs;
    if (!submitButton) return;

    const { status } = this.refs;
    if (status && isLoading) {
      status.hidden = true;
    }

    submitButton.toggleAttribute('disabled', isLoading);

    const loadingLabel = submitButton.dataset.loadingLabel || 'Adding...';
    const submitLabel = submitButton.dataset.submitLabel || this.#defaultSubmitLabel || 'Add selected items';

    submitButton.textContent = isLoading ? loadingLabel : submitLabel;
  }

  /**
   * Syncs the select-all button label and disabled state.
   */
  #syncSelectAllButton() {
    const { selectAllButton } = this.refs;
    if (!selectAllButton) return;

    const availableCheckboxes = this.#availableCheckboxes();
    const hasOptions = availableCheckboxes.length > 0;

    selectAllButton.toggleAttribute('disabled', !hasOptions);

    if (!hasOptions) {
      selectAllButton.textContent = selectAllButton.dataset.selectLabel || 'Select all';
      return;
    }

    const allSelected = this.#areAllSelected();
    const label = allSelected
      ? selectAllButton.dataset.clearLabel || 'Clear all'
      : selectAllButton.dataset.selectLabel || 'Select all';

    selectAllButton.textContent = label;
  }

  /**
   * @returns {HTMLInputElement[]}
   */
  #availableCheckboxes() {
    const checkboxes = this.refs.variantCheckboxes ?? [];
    return Array.from(checkboxes).filter((checkbox) => !checkbox.disabled);
  }

  /** Syncs the visual state of variant rows with checkbox selection. */
  #syncVariantRows() {
    const checkboxes = this.refs.variantCheckboxes ?? [];

    for (const checkbox of Array.from(checkboxes)) {
      const container = checkbox.closest('.quick-reorder__variant');
      if (!(container instanceof HTMLElement)) continue;

      container.classList.toggle('is-selected', checkbox.checked);
    }
  }

  /**
   * @returns {boolean}
   */
  #areAllSelected() {
    const checkboxes = this.#availableCheckboxes();
    if (checkboxes.length === 0) return false;
    return checkboxes.every((checkbox) => checkbox.checked);
  }

  /**
   * @param {string | undefined} variantId
   * @returns {HTMLInputElement | undefined}
   */
  #getCheckboxForVariant(variantId) {
    if (!variantId) return undefined;
    const checkboxes = this.refs.variantCheckboxes ?? [];
    return Array.from(checkboxes).find((checkbox) => checkbox.dataset.variantId === variantId);
  }

  /**
   * Formats cents into a money string using the theme utilities.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    if (window.Theme?.utilities?.formatMoney) {
      return window.Theme.utilities.formatMoney(cents);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }
}

if (!customElements.get('quick-reorder-component')) {
  customElements.define('quick-reorder-component', QuickReorderComponent);
}
